import React from "react";
import { PanelHeader } from "./ScenarioPanel";
import { ShieldCheck, ShieldWarning, Prohibit, UserCheck, ArrowsClockwise } from "@phosphor-icons/react";

const AUTH_TONE = {
  pass: { bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200", icon: <ShieldCheck size={12} weight="fill" /> },
  conditional: { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", icon: <ShieldWarning size={12} weight="fill" /> },
  blocked: { bg: "bg-rose-50", text: "text-rose-800", border: "border-rose-200", icon: <Prohibit size={12} weight="fill" /> },
};

const STATUS_TONE = {
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  "advisory-allowed": "bg-emerald-50 text-emerald-800 border-emerald-200",
  escalated: "bg-amber-50 text-amber-800 border-amber-200",
  held: "bg-rose-50 text-rose-800 border-rose-200",
  denied: "bg-rose-50 text-rose-800 border-rose-200",
};

export const KatlasPanel = ({ katlas, request }) => {
  if (!katlas || !request) return null;
  const auth = AUTH_TONE[katlas.authority.state];

  return (
    <section
      data-testid="katlas-panel"
      className="panel-in w-[340px] shrink-0 h-full flex flex-col bg-white border border-stone-200 rounded-sm"
    >
      <PanelHeader layer="katlas" title="KATLAS · Operational Governance" subtitle="Runtime authority, review thresholds, handoff policy" />

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 scroll-soft">
        {/* Requesting role + action */}
        <div className="border border-stone-200 rounded-sm p-3 bg-stone-50/40">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Requesting role" value={request.ownerRole} />
            <Field label="Class" value={request.flow} />
          </div>
          <div className="mt-2 pt-2 border-t border-stone-200">
            <div className="text-[11px] uppercase tracking-wider text-stone-500">Proposed action</div>
            <div className="text-sm text-stone-900 mt-0.5">{request.proposedAction}</div>
          </div>
        </div>

        {/* Operational authority check */}
        <div data-testid="katlas-authority" className={`border rounded-sm p-3 ${auth.bg} ${auth.border}`}>
          <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium ${auth.text}`}>
            {auth.icon} Operational authority check · {katlas.authority.state.toUpperCase()}
          </div>
          <p className={`text-xs ${auth.text} mt-1 leading-relaxed`}>{katlas.authority.note}</p>
        </div>

        {/* Human review threshold */}
        <div className="border border-stone-200 rounded-sm p-3">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-stone-500">
            <UserCheck size={12} /> Human review threshold
          </div>
          <div className="text-sm text-stone-900 mt-1 capitalize">{katlas.approval.level.replace(/-/g, " ")}</div>
          {katlas.approval.reviewers.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {katlas.approval.reviewers.map((r) => (
                <span key={r} className="text-[10px] font-mono px-1.5 py-0.5 bg-stone-100 text-stone-700 rounded-sm">
                  {r}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Disclosure / handoff */}
        <div className="border border-stone-200 rounded-sm p-3">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-stone-500">
            <ArrowsClockwise size={12} /> External handoff &amp; disclosure
          </div>
          <p className="text-xs text-stone-800 mt-1 leading-relaxed">{katlas.disclosure.policy}</p>
          <div className="mt-1.5 text-[11px] font-mono">
            {katlas.disclosure.allowed ? (
              <span className="text-emerald-700">handoff permitted under selective disclosure</span>
            ) : (
              <span className="text-rose-700">external handoff restricted — partner consent required</span>
            )}
          </div>
        </div>

        {/* Status */}
        <div data-testid="katlas-status" className={`border rounded-sm p-3 ${STATUS_TONE[katlas.status]}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider font-medium">Governed action outcome</span>
            <span className="text-xs font-mono uppercase">{katlas.status}</span>
          </div>
          {katlas.escalation && (
            <p className="text-xs mt-1.5 leading-relaxed">{katlas.escalation}</p>
          )}
        </div>

        {/* Decision card / receipt */}
        <DecisionCard katlas={katlas} request={request} />
      </div>
    </section>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-stone-500">{label}</div>
    <div className="text-sm text-stone-900 mt-0.5 capitalize">{value}</div>
  </div>
);

export const DecisionCard = ({ katlas, request }) => (
  <div className="receipt-in relative bg-[#FFFCF8] border border-dashed border-stone-300 rounded-sm p-4 paper-texture overflow-hidden">
    <div className="relative z-10">
      <div className="flex items-start justify-between border-b border-stone-200 pb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500">
            KATLAS · Decision record
          </div>
          <div className="font-serif italic text-lg text-stone-900 leading-tight mt-0.5">
            Attributable receipt
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-stone-500">Ref</div>
          <div className="font-mono text-[11px] text-stone-900">{katlas.decisionId}</div>
        </div>
      </div>

      <dl className="font-mono text-[11px] text-stone-700 space-y-1 mt-2.5">
        <Row k="role" v={request.ownerRole} />
        <Row k="action" v={request.proposedAction} />
        <Row k="authority" v={katlas.authority.state} />
        <Row k="approval" v={katlas.approval.level} />
        <Row k="reviewers" v={katlas.approval.reviewers.join(", ") || "—"} />
        <Row k="disclosure" v={katlas.disclosure.policy} />
        <Row k="status" v={katlas.status} />
        <Row k="issued" v={new Date(katlas.issuedAt).toLocaleString()} />
      </dl>

      <div className="mt-3 pt-2 border-t border-stone-200">
        <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Rationale</div>
        <ul className="text-[11px] text-stone-700 leading-relaxed space-y-0.5 list-disc list-inside">
          {katlas.rationale.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      </div>

      <div className="absolute bottom-2 right-2 text-[10px] uppercase tracking-[0.16em] text-emerald-700/70 font-medium">
        Attributable
      </div>
    </div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex gap-3">
    <span className="text-stone-400 w-16 shrink-0">{k}</span>
    <span className="text-stone-800 flex-1 break-words">{v}</span>
  </div>
);
